import { z } from 'zod';

// Adapted from https://github.com/astahmer/openapi-zod-client/blob/main/examples/export-schemas-and-types-directly/schemas-and-types-directly.hbs

const IdString = z.string();
const AssetId = z.object({ asset_id: IdString }).partial().passthrough();
const LicensePlate = z
    .object({
        license_plate: z.object({ plate_number: z.string(), country_code: z.string() }).passthrough(),
    })
    .partial()
    .passthrough();
const Trailer = AssetId.and(LicensePlate);
const AdditionalCustomerData = z.record(z.string());
const TourStatus = z.string();
const WriteDocument = z
    .object({
        id: IdString,
        type: z.string(),
        name: z.string(),
        file_name: z.string(),
        external_id: z.string().optional(),
    })
    .passthrough();
const DocumentAssociation = z
    .object({
        type: z.enum([
            'tour',
            'tour-stop',
            'service-order',
            'transport-order',
            'problem',
            'handling-unit',
            'bulk-material-unit',
        ]),
        id: IdString,
    })
    .partial()
    .passthrough();
const Document = WriteDocument.and(
    z
        .object({ associations: z.array(DocumentAssociation) })
        .partial()
        .passthrough()
);
const Documents = z.array(Document);
const Timestamp = z.string();
const ServiceOrder = z
    .object({
        id: IdString,
        external_id: z.string().optional(),
        order: z.string(),
        note: z.string().optional(),
        secondary_reference_id: z.string().optional(),
        additional_customer_data: AdditionalCustomerData.optional(),
        documents: Documents.optional(),
        started_at: Timestamp.optional(),
        finished_at: Timestamp.optional(),
        added_at: Timestamp.optional(),
        cancelled_at: Timestamp.optional(),
    })
    .passthrough();
const Contact = z
    .object({
        name: z.string(),
        email: z.string(),
        phone: z.string(),
        language: z.string(),
        has_agreed_to_receive_emails: z.boolean(),
    })
    .partial()
    .passthrough();
const Loading = z.object({ note: z.string() }).partial().passthrough();
const Unloading = z.object({ note: z.string() }).partial().passthrough();
const GoodsBase = z.object({ type: z.string() }).passthrough();
const Dimension = z
    .object({
        length_cm: z.number(),
        width_cm: z.number(),
        height_cm: z.number(),
    })
    .passthrough();
const Weight = z.object({ weight_kg: z.number() }).passthrough();
const LoadingMeter = z.object({ value: z.number() }).passthrough();
const ArticleQuantityInteger = z.object({ type: z.string(), value: z.number().int() }).passthrough();
const ArticleQuantityDecimal = z.object({ type: z.string(), value: z.number() }).passthrough();
const ArticleQuantity = z.union([ArticleQuantityInteger, ArticleQuantityDecimal]);
const Article = z
    .object({
        customer_article_id: z.string(),
        quantity: ArticleQuantity,
        name: z.string(),
        article_id: z.string(),
    })
    .partial()
    .passthrough();
const HandlingUnitDeviation = z
    .object({
        quantity: z.number().int(),
        weight: Weight,
        loading_meter: LoadingMeter,
        articles: z.array(Article),
        comment: z.string(),
    })
    .partial()
    .passthrough();
const ProofOfHandling = z
    .object({
        confirmed_at: Timestamp,
        deviation: HandlingUnitDeviation.optional(),
    })
    .passthrough();
const HandlingUnit = z
    .object({
        id: IdString,
        packaging_type: z.string(),
        quantity: z.number().int(),
        stacking_factor: z.number().int().optional(),
        dimension: Dimension.optional(),
        weight: Weight.optional(),
        loading_meter: LoadingMeter.optional(),
        articles: z.array(Article).optional(),
        documents: Documents.optional(),
        reported_at: Timestamp.optional(),
        proof_of_loading: ProofOfHandling.optional(),
        proof_of_delivery: ProofOfHandling.optional(),
    })
    .passthrough();
const CargoMaterial = GoodsBase.and(
    z
        .object({
            type: z.literal('cargo-material').optional(),
            handling_units: z.array(HandlingUnit),
        })
        .passthrough()
);
const CustomerTrailerProofOfHandling = z
    .object({ confirmed_at: Timestamp, license_plate: z.string().optional() })
    .passthrough();
const TrailerUnit = z
    .object({
        id: z.string(),
        license_plate: z.string().optional(),
        note: z.string().optional(),
        proof_of_loading: CustomerTrailerProofOfHandling.optional(),
        proof_of_delivery: CustomerTrailerProofOfHandling.optional(),
    })
    .passthrough();
const CustomerTrailer = GoodsBase.and(
    z
        .object({
            type: z.literal('customer-trailer').optional(),
            trailers: z.array(TrailerUnit),
        })
        .passthrough()
);
const SwapBodyProofOfHandling = z
    .object({
        confirmed_at: Timestamp,
        swap_body_reference: z.string().optional(),
    })
    .passthrough();
const SwapBodyUnit = z
    .object({
        id: z.string(),
        proof_of_loading: SwapBodyProofOfHandling.optional(),
        proof_of_delivery: SwapBodyProofOfHandling.optional(),
    })
    .passthrough();
const SwapBody = GoodsBase.and(
    z
        .object({
            type: z.literal('swap-body').optional(),
            units: z.array(SwapBodyUnit),
        })
        .passthrough()
);
const MaterialQuantity = z.object({ type: z.string(), value: z.number() }).passthrough();
const Material = z.object({ id: z.string(), name: z.string() }).passthrough();
const BulkMaterialUnitDeviation = z.object({ quantity: MaterialQuantity, comment: z.string() }).partial().passthrough();
const BulkMaterialUnitProofOfHandling = z
    .object({
        confirmed_at: Timestamp,
        deviation: BulkMaterialUnitDeviation.optional(),
    })
    .passthrough();
const BulkMaterialUnit = z
    .object({
        id: IdString,
        quantity: MaterialQuantity,
        material: Material,
        documents: Documents.optional(),
        reported_at: Timestamp.optional(),
        proof_of_loading: BulkMaterialUnitProofOfHandling.optional(),
        proof_of_delivery: BulkMaterialUnitProofOfHandling.optional(),
    })
    .passthrough();
const BulkMaterial = GoodsBase.and(
    z
        .object({
            type: z.literal('bulk-material').optional(),
            units: z.array(BulkMaterialUnit),
        })
        .passthrough()
);
const Goods = z.union([CargoMaterial, CustomerTrailer, SwapBody, BulkMaterial]);
const Coordinates = z.object({ latitude: z.number(), longitude: z.number() }).passthrough();
const SignatureCapture = z
    .object({
        document: Document,
        taken_at: Timestamp,
        taken_at_position: Coordinates.optional(),
    })
    .passthrough();
const TransportOrderConfirmation = z
    .object({
        driver_name: z.string().optional(),
        customer_name: z.string().optional(),
        customer_email: z.string().optional(),
        triggered_at: Timestamp,
        triggered_at_position: Coordinates.optional(),
        stop_id: IdString,
        driver_signature: SignatureCapture.optional(),
        customer_signature: SignatureCapture.optional(),
    })
    .passthrough();
const TransportOrder = z
    .object({
        id: IdString,
        external_id: z.string().optional(),
        reference_id: z.string().optional(),
        secondary_reference_id: z.string().optional(),
        additional_customer_data: AdditionalCustomerData.optional(),
        contact: Contact.optional(),
        loading: Loading.optional(),
        unloading: Unloading.optional(),
        goods: Goods.optional(),
        documents: Documents.optional(),
        loaded_at: Timestamp.optional(),
        delivered_at: Timestamp.optional(),
        added_at: Timestamp.optional(),
        cancelled_at: Timestamp.optional(),
        loading_confirmation: TransportOrderConfirmation.optional(),
        unloading_confirmation: TransportOrderConfirmation.optional(),
    })
    .passthrough();
const MeansOfTransport = AssetId.and(LicensePlate);
const Address = z
    .object({
        street: z.string(),
        postal_code: z.string(),
        city: z.string(),
        country: z.string(),
    })
    .partial()
    .passthrough();
const Location = z
    .object({
        name: z.string().optional(),
        address: Address.optional(),
        coordinates: Coordinates,
        external_id: z.string().optional(),
    })
    .passthrough();
const TourStop = z
    .object({
        id: IdString,
        external_id: z.string().optional(),
        planned_arrival_at: Timestamp,
        planned_departure_at: Timestamp,
        location: Location,
        additional_customer_data: AdditionalCustomerData.optional(),
        load: z.array(IdString).optional(),
        unload: z.array(IdString).optional(),
        execute: z.array(IdString).optional(),
        documents: Documents.optional(),
        reached_at: Timestamp.optional(),
        unloading_started_at: Timestamp.optional(),
        unloading_finished_at: Timestamp.optional(),
        loading_started_at: Timestamp.optional(),
        loading_finished_at: Timestamp.optional(),
        execution_started_at: Timestamp.optional(),
        execution_finished_at: Timestamp.optional(),
        finished_at: Timestamp.optional(),
        added_at: Timestamp.optional(),
        cancelled_at: Timestamp.optional(),
        skipped_at: Timestamp.optional(),
    })
    .passthrough();
const TourProblemType = z.string();
const WriteDocuments = z.array(WriteDocument);
const TourProblem = z
    .object({
        id: IdString,
        type: TourProblemType,
        tour_stop_id: IdString.optional(),
        documents: WriteDocuments.optional(),
        reported_at: Timestamp.optional(),
        acknowledged_at: Timestamp.optional(),
    })
    .passthrough();
const TourProblems = z.array(TourProblem);
const Tour = z
    .object({
        id: IdString,
        external_id: z.string().optional(),
        name: z.string().optional(),
        account_id: IdString,
        note: z.string().optional(),
        trailer: Trailer.optional(),
        drivers: z.array(z.string()),
        additional_customer_data: AdditionalCustomerData.optional(),
        status: TourStatus,
        service_orders: z.array(ServiceOrder).optional(),
        transport_orders: z.array(TransportOrder).optional(),
        means_of_transport: MeansOfTransport.optional(),
        stops: z.array(TourStop),
        problems: TourProblems.optional(),
        documents: Documents.optional(),
        seen_at: Timestamp.optional(),
        started_at: Timestamp.optional(),
        finished_at: Timestamp.optional(),
        etag: z.string(),
    })
    .passthrough();
const TourCursor = z
    .object({ items: z.array(Tour), next: z.string() })
    .partial()
    .passthrough();

export const schemas = {
    IdString,
    AssetId,
    LicensePlate,
    Trailer,
    AdditionalCustomerData,
    TourStatus,
    WriteDocument,
    DocumentAssociation,
    Document,
    Documents,
    Timestamp,
    ServiceOrder,
    Contact,
    Loading,
    Unloading,
    GoodsBase,
    Dimension,
    Weight,
    LoadingMeter,
    ArticleQuantityInteger,
    ArticleQuantityDecimal,
    ArticleQuantity,
    Article,
    HandlingUnitDeviation,
    ProofOfHandling,
    HandlingUnit,
    CargoMaterial,
    CustomerTrailerProofOfHandling,
    TrailerUnit,
    CustomerTrailer,
    SwapBodyProofOfHandling,
    SwapBodyUnit,
    SwapBody,
    MaterialQuantity,
    Material,
    BulkMaterialUnitDeviation,
    BulkMaterialUnitProofOfHandling,
    BulkMaterialUnit,
    BulkMaterial,
    Goods,
    Coordinates,
    SignatureCapture,
    TransportOrderConfirmation,
    TransportOrder,
    MeansOfTransport,
    Address,
    Location,
    TourStop,
    TourProblemType,
    WriteDocuments,
    TourProblem,
    TourProblems,
    Tour,
    TourCursor,
};
